import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiErrorDetail, resolveProfileImage } from "@/lib/api";
import { ArrowRight, User as UserIcon, Eye, EyeOff } from "lucide-react";

const REMEMBERED_KEY = "hassein.remembered.email";

export default function Login() {
  const { login } = useAuth();
  const remembered = typeof window !== "undefined" ? localStorage.getItem(REMEMBERED_KEY) || "" : "";
  const [email, setEmail] = useState(remembered);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [quick, setQuick] = useState(null);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return "בוקר טוב";
    if (h < 17) return "צהריים טובים";
    if (h < 21) return "ערב טוב";
    return "לילה טוב";
  }, []);

  useEffect(() => {
    if (!remembered) return;
    (async () => {
      try {
        const res = await api.get("/auth/quick-login-info", { params: { email: remembered } });
        if (res.data.exists) setQuick(res.data);
      } catch { /* noop */ }
    })();
  }, [remembered]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      if (rememberMe) {
        localStorage.setItem(REMEMBERED_KEY, email);
      } else {
        localStorage.removeItem(REMEMBERED_KEY);
      }
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const forgetMe = () => {
    localStorage.removeItem(REMEMBERED_KEY);
    setQuick(null);
    setEmail("");
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background:
          "radial-gradient(1200px 600px at 90% -10%, #E7F1EA 0%, transparent 60%), radial-gradient(900px 500px at -10% 100%, #FCE7D8 0%, transparent 55%), #F9FAF9",
      }}
    >
      <form onSubmit={submit} className="card-surface p-8 md:p-10 grain w-full max-w-md" data-testid="login-form">
        {quick ? (
          <div className="flex flex-col items-center text-center mb-6">
            {quick.profile_image ? (
              <img
                src={resolveProfileImage(quick.profile_image)}
                alt={quick.full_name}
                className="w-24 h-24 rounded-full object-cover border-4 border-white shadow-lg bg-slate-100"
                data-testid="quick-login-photo"
                onError={(e) => {
                  // Fallback to letter avatar if the image URL is broken
                  e.currentTarget.style.display = "none";
                  const fb = e.currentTarget.nextElementSibling;
                  if (fb) fb.style.display = "flex";
                }}
              />
            ) : null}
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center text-3xl font-bold text-white"
              style={{ background: "var(--brand)", display: quick.profile_image ? "none" : "flex" }}
              data-testid="quick-login-photo-fallback"
            >
              {quick.full_name?.[0] || "?"}
            </div>
            <div className="mt-3 font-heebo font-bold text-2xl text-slate-900" data-testid="quick-login-greeting">
              {greeting}, <span style={{ color: "var(--brand)" }}>{quick.full_name}</span>
            </div>
            <div className="text-sm text-slate-500 font-heebo mt-1">
              {quick.gender === "female" ? "התחברי" : "התחבר"} כדי להמשיך
            </div>
            <button type="button" onClick={forgetMe} className="text-xs text-slate-400 hover:text-slate-700 mt-2 font-heebo" data-testid="quick-login-forget">
              לא {quick.full_name}? התחבר עם חשבון אחר
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-3 mb-6">
            <div>
              <div className="font-heebo font-bold text-xl">התחברות</div>
              <div className="text-sm text-slate-500">גישה לאזור האישי שלך</div>
            </div>
          </div>
        )}

        {!quick && (
          <label className="block mb-4">
            <div className="text-xs text-slate-500 mb-1 font-heebo">אימייל</div>
            <input type="email" className="input-field" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" dir="ltr" />
          </label>
        )}
        <label className="block mb-4">
          <div className="text-xs text-slate-500 mb-1 font-heebo">סיסמה</div>
          <div className="relative">
            <input
              type="text"
              className={`input-field pr-12 ${showPassword ? "" : "pw-masked"}`}
              autoComplete="off"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus={!!quick}
              data-testid="login-password-input"
              dir="ltr"
              style={{ paddingRight: "3rem" }}
              data-lpignore="true"
              data-1p-ignore="true"
              data-bwignore="true"
              data-form-type="other"
              name="pw-hassein"
              role="textbox"
              aria-label="סיסמה"
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute inset-y-0 right-2 flex items-center px-2 text-slate-500 hover:text-slate-800 transition"
              aria-label={showPassword ? "הסתר סיסמה" : "הצג סיסמה"}
              data-testid="login-toggle-password"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </label>

        <label className="flex items-center gap-2 mb-6 cursor-pointer" data-testid="login-remember-me-label">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="w-4 h-4 accent-emerald-600 cursor-pointer"
            data-testid="login-remember-me"
          />
          <span className="text-sm text-slate-600 font-heebo select-none">
            זכור אותי לכניסות הבאות
          </span>
        </label>

        {err && <div className="mb-4 chip chip-danger" data-testid="login-error">{err}</div>}

        <button type="submit" disabled={busy} className="btn-primary w-full justify-center" data-testid="login-submit-button">
          {busy ? "מתחבר…" : "כניסה"}
        </button>

        <div className="mt-3 text-center">
          <Link
            to="/forgot-password"
            className="text-sm font-heebo text-slate-500 hover:text-slate-800 transition"
            data-testid="link-to-forgot"
          >
            שכחת סיסמה?
          </Link>
        </div>

        <div className="mt-6 text-center text-sm">
          <span className="text-slate-500 font-heebo">אין לך חשבון? </span>
          <Link to="/register" className="font-heebo font-semibold" style={{ color: "var(--brand)" }} data-testid="link-to-register">
            הירשם כאן <ArrowRight size={14} className="inline" />
          </Link>
        </div>
        <div className="mt-2 text-center">
          <Link to="/" className="text-xs text-slate-400 hover:text-slate-600 font-heebo" data-testid="link-to-landing">
            <UserIcon size={12} className="inline" /> חזרה לעמוד הבית
          </Link>
        </div>
      </form>
    </div>
  );
}
